/**
 * Current User Management
 *
 * Saving, updating and getting the current user can be done here.
 */

import { reactive, computed, watch, onBeforeUnmount, ref } from "vue";
import { setLocale } from "@/i18n";
import { NoRefreshTokenError } from "@core/errors/Errors.js";
import { transferLegacyData } from "@/api/DB";
import * as API from "@/api/API";
import envVars from "@/helpers/Env";
import eventHub from "@/helpers/utility/Events";
import envOptions from "@/helpers/Env";
import type { User } from "./User";

let currentLoadingProcess: Promise<User> | null = null;

const user = ref<User | null>(null);

const _isSyncEnabled = ref<boolean | null>(null);

// Send an event every time the user changes
watch(
  () => user.value?.id,
  () => {
    eventHub.emit("user-changed", user.value);
    console.log("user changed to", user.value);
  }
);

export function useUser() {
  function setUser(_user: User | null) {
    user.value = _user;

    if (envOptions.saveLocally) {
      if (_user == null) {
        console.log(
          "%cREMOVING last user entry",
          "padding: 1rem 2rem; background-color: lightred;"
        );
        localStorage.removeItem("lastUser");
      } else {
        const userAsString = JSON.stringify(_user);
        localStorage.setItem("lastUser", userAsString);
      }
    }

    if (_user?.language != null) {
      console.log("set locale from user entry:", _user.language);
      setLocale(_user.language, false);
    }

    _isSyncEnabled.value = null;
  }

  async function getOrLoadUser(requestOptions: API.RequestOptions<User> = {}) {
    if (user.value == null) {
      return await loadUser(requestOptions);
    }
    return user.value;
  }

  /**
   * Loads the user from the appropriate place and returns it.
   */
  async function loadUser(requestOptions: API.RequestOptions<User> = {}) {
    if (currentLoadingProcess != null) {
      return currentLoadingProcess;
    }

    const loadingProcess = _loadUser(requestOptions).finally(
      () => (currentLoadingProcess = null)
    );
    currentLoadingProcess = loadingProcess;
    return loadingProcess;
  }

  /**
   * Loads the user from the appropriate place and returns it.
   */
  async function _loadUser(requestOptions: API.RequestOptions<User> = {}) {
    try {
      const user = await API.get<User>("/v1/user", {
        ...requestOptions,
        async synch(makeRequest) {
          try {
            return await makeRequest();
          } catch (_error) {
            const error = _error as { status?: number };
            // No connection possible?
            // Try restoring the last user.
            if (error.status == null) {
              const savedUser = getUserFromLocalStorage();
              if (savedUser == null) {
                console.warn("Could not restore saved user.");
                throw new NoRefreshTokenError();
              }

              console.log(
                "%cfrom cache: user",
                "background-color: yellow; padding: 5px 1rem;"
              );

              return savedUser;
            } else {
              throw error;
            }
          }
        }
      });

      if (envOptions.saveLocally) {
        // This transfers the data from the old pouchDB database
        // to the new dexie user database.
        await transferLegacyData(user);
      }

      setUser(user);

      return user;
    } catch (_error) {
      const error = _error as { status?: string; name: string };
      console.log(error.status, error.name);
      throw error;
    }
  }

  async function login(email: string, password: string) {
    if (!email || !password) {
      throw new Error("E-Mail and password have to be set.");
    }

    await auth(email, password);

    const user = await loadUser();
    eventHub.emit("login", user);

    if (envOptions.saveLocally) {
      await transferLegacyData(user);
    }

    setUser(user);

    // we ONLY save login data in the local storage
    // if we are in electron.
    if (envVars.isElectron && window.electron) {
      try {
        setLoginEncrypted(email, password);
      } catch (error) {
        console.error(error);
      }
    }

    return user;
  }

  /**
   * Log in a user using username and password
   * Save the returned tokens.
   */
  async function auth(email: string, password: string) {
    const response = await API.post<API.AuthData>("/auth/", {
      sendAuthToken: false,
      data: {
        grant_type: "password",
        username: email,
        password
      }
    });
    API.saveToken(response);

    return true;
  }

  /**
   * Remove the user tokens from localStorage,
   * removes any user data from memory and
   * emits the logout event.
   */
  function logout() {
    // remove password from saved login data
    setLoginEncrypted(user.value?.email);

    API.destroy("/auth/", {
      sendAuthToken: false,
      data: {
        refresh_token: API.getRefreshToken()
      }
    });
    API.setToken(null);
    API.setRefreshToken(null);

    setUser(null);
    eventHub.emit("logout");
  }

  function getUserFromLocalStorage() {
    const userAsString = localStorage.getItem("lastUser");
    if (userAsString == null) return null;
    return JSON.parse(userAsString);
  }

  /**
   * Get information about all users the user already has connections to.
   */
  async function getKnownUsers(includeMyself = false) {
    const users = await API.get<User[]>("/users_list/");
    if (includeMyself && user.value) {
      users.push(user.value);
    }
    return users;
  }

  /**
   * Called when the user has changed.
   */
  function onUserChange(callback: (data: User) => void) {
    eventHub.on("user-changed", callback);
    onBeforeUnmount(() => {
      eventHub.off("user-changed", callback);
    });
  }

  async function updateUser(
    userData: Partial<User> & { password?: string; acceptTerms?: boolean },
    requestOptions = {}
  ) {
    const _user = await API.patch<User>("/v1/user", {
      ...requestOptions,
      data: {
        ...userData
      }
    });

    setUser(_user);
    eventHub.emit("profile-updated", user.value);

    return user.value!;
  }

  /**
   * Log in with a one-time-login link. Returns the user.
   */
  const loginWithToken = async function (
    uid: string,
    token: string,
    type: string | null = null,
    relatedPk: string | null = null
  ) {
    let url = `/v1/auth-link/${uid}/${token}`;
    if (type) {
      url += `/${type}/${relatedPk}`;
    }
    const authData = await API.post<API.AuthData>(url, {
      sendAuthToken: false
    });
    API.saveToken(authData);

    const user = await loadUser();
    setUser(user);

    return user;
  };

  function isSyncEnabled() {
    if (!envOptions.saveLocally) return false;
    if (!envOptions.synchronizeSaves) return false;
    if (user.value == null) return null;

    const data = localStorage.getItem(`user-${user.value.id}-synch-enabled`);
    if (data == null) return null;
    return JSON.parse(data) === true;
  }

  const syncEnabled = computed({
    get: () => {
      if (_isSyncEnabled.value == null) {
        _isSyncEnabled.value = isSyncEnabled();
      }
      return _isSyncEnabled.value;
    },
    set: (enabled) => {
      _isSyncEnabled.value = enabled;
      if (user.value) {
        localStorage.setItem(
          `user-${user.value.id}-synch-enabled`,
          `${enabled}`
        );
      }
    }
  });

  return {
    user,

    onUserChange,
    setUser,
    updateUser,
    getKnownUsers,
    getOrLoadUser,
    loadUser,
    login,
    logout,
    loginWithToken,

    syncEnabled,
    isSyncEnabled
  };
}

async function setLoginEncrypted(email?: string, password?: string | null) {
  const electron = window.electron;
  if (!electron) return null;

  if (email == null) {
    localStorage.removeItem("login");
    return;
  }

  const loginData: { email: string; password?: string } = {
    email
  };
  if (password) loginData.password = password;

  const credentials = JSON.stringify({
    email: email,
    password: password
  });

  const encryptedData = await electron.encryptCredential(credentials);

  localStorage.setItem("login", encryptedData);
}

export function useUserContext() {
  return reactive(useUser());
}
